import Controller from "@ember/controller";
import { task } from "ember-concurrency";
import { service } from "@ember/service";
import { action } from "@ember/object";
import { tracked } from "@glimmer/tracking";
import { saveRecord } from "@warp-drive/legacy/compat/builders";

export default class OrganizationsCreateController extends Controller {
  @service router;
  @service store;
  @service intl;
  @service modal;
  @service paymentCard;
  @service errorHandler;

  get initialStepIndex() {
    return this.organizationId ? 1 : 0;
  }

  get effectiveOrganizationType() {
    return this.organizationType || this.createdOrganization?.type;
  }

  get effectiveOrganizationName() {
    return this.organizationName ?? this.createdOrganization?.name;
  }

  get steps() {
    return [
      {
        title: this.intl.t("organization.create.labels.get-started"),
        canProceed: this.step1IsValid,
      },
      {
        title: this.intl.t("organization.create.labels.add-details"),
        canProceed: this.step2IsValid,
        nextButtonLabel: this.organizationId
          ? this.intl.t("organization.create.update-and-continue-button")
          : this.intl.t("organization.create.cta-button"),
        allowBack: !this.createdOrganization && !this.createOrganizationTask.isRunning && !this.organizationId,
      },
      {
        title: this.intl.t("organization.create.labels.add-payment-method"),
        canProceed: this.step3IsValid,
        finishButtonLabel: this.finishButtonLabel,
        allowBack: false,
      },
    ];
  }

  get step1IsValid() {
    return !!this.organizationType;
  }

  get organizationAddressIsValid() {
    const length = this.organizationAddress?.length || 0;
    return length >= 5;
  }

  get organizationCityIsValid() {
    const length = this.organizationCity?.length || 0;
    return length >= 3;
  }

  get organizationZipIsValid() {
    const length = this.organizationZip?.length || 0;
    return length >= 3;
  }

  get organizationVatIsValid() {
    const length = this.organizationVatNumber?.length || 0;
    return length >= 7;
  }

  get step2IsValid() {
    const vatIsValid = !this.organizationVatNumber || this.organizationVatIsValid;
    const countryIsValid = this.organizationId || this.organizationCountry;

    return !!(
      this.effectiveOrganizationName &&
      this.organizationIdentityNumber &&
      countryIsValid &&
      this.organizationCityIsValid &&
      this.organizationZipIsValid &&
      this.organizationAddressIsValid &&
      this.organizationPhone &&
      vatIsValid
    );
  }

  get step3IsValid() {
    if (!this.organizationPaymentMethod) {
      return false;
    }

    if (this.selectedPaymentMethodIsCard) {
      return !!this.paymentCardUrl;
    }

    return !this.submitInvoicePaymentTask.isRunning;
  }

  get finishButtonLabel() {
    if (this.cardPaymentFlowStarted) {
      return this.intl.t("button.go-to-overview");
    }

    if (this.selectedPaymentMethodIsInvoice) {
      return this.intl.t("button.finish");
    }

    return this.intl.t("button.continue");
  }

  get countries() {
    return this.model.countries;
  }

  get cardPaymentIsAvailable() {
    return this.createdOrganization?.availablePaymentMethods?.includes("card");
  }

  get invoicePaymentIsAvailable() {
    return this.createdOrganization?.availablePaymentMethods?.includes("invoice");
  }

  get selectedPaymentMethodIsCard() {
    return this.organizationPaymentMethod === "card";
  }

  get selectedPaymentMethodIsInvoice() {
    return this.organizationPaymentMethod === "invoice";
  }

  get handleNextLoading() {
    return this.createOrganizationTask.isRunning || this.editOrganizationTask.isRunning;
  }

  get handleFinishLoading() {
    return this.submitInvoicePaymentTask.isRunning || this.fetchPaymentCardUrl.isRunning;
  }

  get canShowPaymentLink() {
    return this.createdOrganization && this.editOrganizationTask.lastSuccessful;
  }

  get showSkipPaymentLink() {
    return this.canShowPaymentLink && !this.submitInvoicePaymentTask.lastSuccessful && !this.cardPaymentFlowStarted;
  }

  get showPaymentCardLink() {
    return this.canShowPaymentLink && this.selectedPaymentMethodIsCard && this.cardPaymentFlowStarted;
  }

  queryParams = ["organizationId"];
  @tracked organizationId = null;
  @tracked organizationName = null;
  @tracked organizationType = null;
  @tracked organizationPaymentMethod = null;
  @tracked organizationCountry = null;
  @tracked organizationIdentityNumber = null;
  @tracked organizationVatNumber = null;
  @tracked organizationAddress = null;
  @tracked organizationCity = null;
  @tracked organizationZip = null;
  @tracked organizationPhone = null;
  @tracked paymentCardUrl = null;
  @tracked createdOrganization = null;
  @tracked cardPaymentFlowStarted = false;

  createOrganizationTask = task(async () => {
    let organization = this.store.createRecord("organization", {
      name: this.organizationName,
      country: this.organizationCountry.code,
      type: this.organizationType,
    });

    await this.store.request(saveRecord(organization)).catch((error) => {
      organization.unloadRecord();
      throw error;
    });

    this.createdOrganization = organization;
    await this.editOrganizationTask.perform();
  });

  editOrganizationTask = task(async () => {
    let organization = this.createdOrganization;

    organization.setProperties({
      name: this.organizationName,
      identityNumber: this.organizationIdentityNumber,
      vatNumber: this.createdOrganization.type === "business" ? this.organizationVatNumber?.trim() : null,
      address: this.organizationAddress,
      city: this.organizationCity,
      zip: this.organizationZip,
      phone: this.organizationPhone,
    });

    await this.store.request(saveRecord(organization)).catch((error) => {
      organization.rollbackAttributes();
      throw error;
    });

    if (this.organizationId) {
      this.organizationId = null;
      this.router.replaceWith("organizations.create", { queryParams: { organizationId: null } });
    }
  });

  fetchPaymentCardUrl = task(async () => {
    this.paymentCardUrl = await this.paymentCard.fetchPaymentCardUrl.perform(this.createdOrganization.id);
  });

  checkForNewAddedCard = task(async () => {
    return this.paymentCard.checkForNewAddedCard.perform(this.createdOrganization);
  });

  submitInvoicePaymentTask = task(async () => {
    let organization = this.createdOrganization;
    organization.set("paymentMethod", "invoice");

    await this.store.request(saveRecord(organization)).catch((error) => {
      organization.rollbackAttributes();
      throw error;
    });

    this.router.transitionTo("organization", organization.id);
  });

  @action
  openCardPaymentModal() {
    window.open(this.paymentCardUrl, "_blank");
    this.modal.open("modals/organization-payment-card", {
      task: this.checkForNewAddedCard,
    });
    this.cardPaymentFlowStarted = true;
  }

  handleOnNext = task(async (stepIndex) => {
    if (stepIndex === 1) {
      try {
        if (!this.createdOrganization) {
          await this.createOrganizationTask.perform();
        } else {
          await this.editOrganizationTask.perform();
        }
      } catch (error) {
        if (this.createdOrganization && !this.organizationId) {
          this.organizationId = this.createdOrganization.id;
          this.router.replaceWith("organizations.create", {
            queryParams: { organizationId: this.createdOrganization.id },
          });
        }

        this.errorHandler.handleError(error);
        return false;
      }
    }
  });

  handleOnFinish = task(async () => {
    if (this.cardPaymentFlowStarted) {
      this.router.transitionTo("organization", this.createdOrganization.id);
      return;
    }

    if (this.selectedPaymentMethodIsInvoice) {
      try {
        await this.submitInvoicePaymentTask.perform();
      } catch (error) {
        this.errorHandler.handleError(error);
        return false;
      }
    }

    if (this.selectedPaymentMethodIsCard) {
      this.openCardPaymentModal();
    }
  });

  @action
  handleStepChange(event) {
    const formData = new FormData(event.currentTarget);

    if (formData.has("organizationName")) {
      this.organizationName = formData.get("organizationName");
      this.organizationIdentityNumber = formData.get("identityNumber");
      this.organizationAddress = formData.get("address");
      this.organizationCity = formData.get("city");
      this.organizationZip = formData.get("zip");
      this.organizationPhone = formData.get("phone");
      this.organizationVatNumber = formData.get("vatNumber");
    }

    if (formData.has("organizationType")) {
      this.organizationType = formData.get("organizationType");
    }

    const countryCode = formData.get("country");
    if (countryCode) {
      this.organizationCountry = this.countries.find((c) => c.code === countryCode);
    }
  }

  @action
  selectPaymentMethod(paymentMethod) {
    this.organizationPaymentMethod = paymentMethod;

    if (paymentMethod === "card" && !this.paymentCardUrl) {
      this.fetchPaymentCardUrl.perform();
    }
  }

  resetController() {
    this.organizationName = null;
    this.organizationType = null;
    this.organizationPaymentMethod = null;
    this.organizationIdentityNumber = null;
    this.organizationVatNumber = null;
    this.organizationAddress = null;
    this.organizationCity = null;
    this.organizationZip = null;
    this.organizationPhone = null;
    this.organizationCountry = null;
    this.paymentCardUrl = null;
    this.createdOrganization = null;
    this.cardPaymentFlowStarted = false;
    this.createOrganizationTask.cancelAll({ resetState: true });
    this.editOrganizationTask.cancelAll({ resetState: true });
    this.submitInvoicePaymentTask.cancelAll({ resetState: true });
    this.fetchPaymentCardUrl.cancelAll({ resetState: true });
  }
}
